import { Component, ChangeDetectionStrategy, input, output, inject, effect, viewChild, afterRenderEffect, untracked } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { first } from 'rxjs/operators';
import { IProfile, ProfileService } from '../profile.service';
import { ResizeService, AuthService } from '../../../auth.service';
import { DateAdapter } from '@angular/material/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatError, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CountryListComponent, NgxCountriesDropdownModule } from 'ngx-countries-dropdown';
import { MatSelectModule } from '@angular/material/select';
import { PaintDirective } from '../../../paint.directive';
import { NgTemplateOutlet } from '@angular/common';
import { MetaService } from '../../../meta.service';
import { LocationService } from '../../../location.service';
// import { Subscription } from 'rxjs';

@Component({
  selector: 'profile-details',
  templateUrl: 'profile-details.component.html',
  styleUrls: ['profile-details.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideLuxonDateAdapter()],
  imports: [MatHint, MatError, MatIcon, MatButtonModule, ReactiveFormsModule, MatDatepickerModule, NgxCountriesDropdownModule, MatSelectModule, PaintDirective, NgTemplateOutlet]
})
export class ProfileDetailsComponent {
  countryInput = viewChild<CountryListComponent>('countryInput');
  countryCodeInput = viewChild<CountryListComponent>('countryCodeInput');
  size = inject(ResizeService);
  service = inject(ProfileService);
  meta = inject(MetaService);

  form = new FormGroup({
    name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    surname: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    email: new FormControl('', { validators: [Validators.required,Validators.email], nonNullable: true }),
    phone: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    gender: new FormControl('Unspecified', { validators: [Validators.required], nonNullable: true }),
    birthday: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    country: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    country_code: new FormControl('356', { validators: [Validators.required], nonNullable: true })
  });

  luxonDateAdapter = inject(DateAdapter);
  dob = new FormControl<DateTime|null>(null);
  minDob: Date = DateTime.now().minus({ years: 120 }).toJSDate()
  maxDob: Date = DateTime.now().minus({ years: 16 }).startOf('day').toJSDate();

  query: string = '';
  queryTimer: any = null;
  now: Date = new Date();
  days: number[] = Array(31).fill(1).map((v, k) => (v * k) + 1);
  months: any[] = Array(12).fill(1).map(function (v, k) {
    const month = (v * k);
    return {
      key: month,
      value: DateTime.now().startOf('year').plus({ month }).toFormat('MMMM')
    };
  });
  years: number[] = Array(101).fill(1).map((v, k) => (v * k) + (this.now.getUTCFullYear() - 110));

  constructor() {
    this.luxonDateAdapter.getFirstDayOfWeek = () => 1;

    this.dob.valueChanges.subscribe(date => {
      if(date){
        this.form.get('birthday')!.setValue(date.toUTC(0,{ keepLocalTime: true }).toFormat('yyyy-MM-dd'));
      }
    });

    afterRenderEffect(() => {
      const details = this.service.profile();

      untracked(() => {
        if(details){
          this.form.reset(details);
          this.dob.reset(details.birthday ? DateTime.fromFormat(details.birthday, 'yyyy-MM-dd', { zone: 'utc' }) : null);
    
          this.form.markAsUntouched();
          this.form.markAsPristine();
    
          if(details.birthday){
            this.form.get('birthday')!.disable();
          } else {
            this.form.get('birthday')!.enable();
          }
        }
      });
    });

    afterRenderEffect(() => {
      const details = this.service.profile();
      const countryInput = this.countryInput();

      untracked(() => {
        if(details?.country && countryInput){
          const selectedCountry = countryInput.countryList().find(country => country.name == details.country);
          if(selectedCountry){
            countryInput.changeCountry(selectedCountry);
          }
        }
      });
    });

    afterRenderEffect(() => {
      const details = this.service.profile();
      const countryCodeInput = this.countryCodeInput();

      untracked(() => {
        if(details?.country_code && countryCodeInput){
          const selectedCountryCode = countryCodeInput.countryList().find(country => country.dialling_code?.replace(/\+/,'') == details.country_code?.replace(/\+/,''));
          if(selectedCountryCode){
            countryCodeInput.changeCountry(selectedCountryCode);
          }
        }
      });
    });
  }

  countSame(): number {
    return Object.keys(this.formChanges()).length;
  }

  formChanges(): Partial<IProfile> {
    return Object.fromEntries(Object.entries(this.form.controls).filter(([key,control]) => control.dirty).map(([key,control]) => ([key,control.value])));
  }

  submit(callback?: Function) {
    if (this.form.valid) {
      const updatedUser = this.formChanges();

      if(!Object.keys(updatedUser).length){
        this.service.toast.info('No changes made');
        return;
      }
      this.service.loading.set(true);

      this.service.editProfile(updatedUser).pipe(first()).subscribe({
        next: res => {
          if (updatedUser.name) {
            this.meta.displayName.set(this.form.get('name')!.value);
            try {
              localStorage?.setItem('user', this.meta.displayName());
            } catch (e){}
          }

          if (updatedUser.email) {
            //when changing email, it's not instant, so revert it back
            updatedUser.email = this.service.profile()?.email;
          }

          this.service.profile.update(user => {
            const mergedUser = Object.assign(Object.assign({}, user), updatedUser) as IProfile;
            return mergedUser;
          });

          if (callback) {
            callback();
          }

          this.service.loading.set(false);

          this.service.toast.info(res.message);
        },
        error: error => {
          error = error.error || error;
          if (error.code == 3) {
            this.service.toast.error('Account deactivated or deleted');
            this.service.router.navigate(['/logout'], { queryParams: { reason: 'Account deactivated or deleted' } });
          }
          else{
            this.service.toast.error(error.message);
          }
          this.service.loading.set(false);
        }
      }
        
      );
    }
    else {
      this.form.markAllAsTouched();
      this.service.toast.error('Not all fields are valid');
    }
  }
}